import { AddressZero } from "@ethersproject/constants"
import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useQueries,
  useQuery,
  UseQueryOptions,
} from "@tanstack/react-query"
import { useActiveWeb3React } from "src/hooks"
import { CRONOS_CRUISER_CONTRACT_ADDRESSES } from "src/constants"
import { BoostFarmQuery } from "src/constants/queryKeys"
import { useEffect } from "react"
import _ from "lodash"

export const INFO_API_ENDPOINT = process.env.REACT_APP_INFO_API!

export const GET_USER_NFT_COLLECTIONS = `nfts`
export const GET_NFT_ASSETS_INFO = `nft`

export enum CHAIN_TYPE {
  CRONOS = "CRONOS",
  ETHEREUM = "ETHEREUM",
}

interface CollectionFilter {
  chain: CHAIN_TYPE
  collections: string[]
  listingType?: "ALL"
}

export interface CollectionParams {
  address: string
  first?: number
  sort?: "HIGHEST_PRICE"
  filter?: CollectionFilter
}

interface Image {
  url: string
}

interface Node {
  image: Image
  name: string
  rarityRank: string // TODO: verify this type
  tokenId: string
}
interface EdgeNode {
  cursor: string
  node: Node
}
interface PageInfo {
  endCursor: string
  hasNextPage: boolean
  hasPreviousPage: boolean
  startCursor: string
}
export interface UserNftCollectionsMeta {
  edges: EdgeNode[]
  totalCount: number
  pageInfo: PageInfo
}

const FetchUserNftCollectionsDefaultParams = {
  first: 50,
  sort: "HIGHEST_PRICE",
  filter: {
    chain: CHAIN_TYPE.CRONOS,
    collections: [],
    listingType: "ALL",
  },
}

export const fetchUserNftCollections = async (
  params: CollectionParams = {
    address: "",
    first: 50,
    sort: "HIGHEST_PRICE",
    filter: {
      chain: CHAIN_TYPE.CRONOS,
      collections: [],
      listingType: "ALL",
    },
  },
  signal?: AbortSignal,
): Promise<UserNftCollectionsMeta> => {
  const res = await fetch(`${INFO_API_ENDPOINT}/${GET_USER_NFT_COLLECTIONS}`, {
    method: "POST",
    headers: {
      Accept: "application.json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      ...FetchUserNftCollectionsDefaultParams,
      ...params,
      filter: {
        ...FetchUserNftCollectionsDefaultParams.filter,
        ...params?.filter,
      },
    }),
    signal,
  })
  return res.json() as Promise<UserNftCollectionsMeta>
}

export const useUserNftCollections = (
  collectionParams: CollectionParams,
  queryOptions?: Partial<Pick<UseQueryOptions, "enabled">>,
) => {
  return useQuery(
    [BoostFarmQuery.INFO_API_META_USER_NFT_COLLECTIONS, collectionParams],
    ({ signal }) => fetchUserNftCollections(collectionParams, signal),
    queryOptions,
  )
}

export const useUserAllNftCollections = (
  collectionParams: CollectionParams,
  queryOptions?: Partial<Pick<UseInfiniteQueryOptions, "enabled">>,
) => {
  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } =
    useInfiniteQuery(
      [BoostFarmQuery.INFO_API_META_USER_NFT_COLLECTIONS],
      ({ pageParam, signal }) =>
        fetchUserNftCollections(
          (pageParam as CollectionParams) ?? collectionParams,
          signal,
        ),
      {
        getNextPageParam: (lastPage) => {
          if (lastPage?.pageInfo?.hasNextPage) {
            return {
              ...collectionParams,
              after: lastPage?.pageInfo?.endCursor,
            }
          }
          return undefined
        },
        ...queryOptions,
      },
    )

  useEffect(() => {
    if (hasNextPage) {
      fetchNextPage()
    }
  }, [data, fetchNextPage, hasNextPage])

  return {
    data,
    isLoading: isFetching || isFetchingNextPage,
    isFetching,
    isFetchingNextPage,
  }
}

interface FetchNftAssetInfoParams {
  nftAddress: string
  chain: CHAIN_TYPE
  tokenId: string
}
interface NftAssetMeta {
  image: Image
  tokenId: string
  name: string
  rarityRank: string // TODO: verify this type
}

export const fetchNftAssetInfo = async (
  params: FetchNftAssetInfoParams = {
    nftAddress: "",
    chain: CHAIN_TYPE.CRONOS,
    tokenId: "",
  },
): Promise<NftAssetMeta> => {
  const { nftAddress, chain, tokenId } = params
  const res = await fetch(
    `${INFO_API_ENDPOINT}/${GET_NFT_ASSETS_INFO}?address=${nftAddress}&chain=${chain}&id=${tokenId}`,
  )
  return res.json() as Promise<NftAssetMeta>
}

export const useNftsAssetInfo = (
  nftsAssetParams: Omit<FetchNftAssetInfoParams, "tokenId"> & {
    tokenIds: string[]
  } = {
    nftAddress: "",
    chain: CHAIN_TYPE.CRONOS,
    tokenIds: [],
  },
  queryOptions?: Partial<Pick<UseQueryOptions, "enabled">>,
) => {
  const { nftAddress, chain, tokenIds } = nftsAssetParams
  return useQueries({
    queries: tokenIds.map((tokenId) => {
      return {
        queryKey: [BoostFarmQuery.INFO_API_META_USER_NFT_BY_ID, tokenId],
        queryFn: () => fetchNftAssetInfo({ nftAddress, tokenId, chain }),
        enabled: (queryOptions?.enabled ?? true) && nftAddress !== AddressZero,
        ...queryOptions,
      }
    }),
  })
}

const useGetUserCronosCruiserParams = (): CollectionParams => {
  const { account, chainId } = useActiveWeb3React()

  if (!account || !chainId) {
    return {
      address: AddressZero,
    }
  }

  const nftCollectionAddress = CRONOS_CRUISER_CONTRACT_ADDRESSES[chainId]
  const userCollectionParams: CollectionParams = {
    address: account,
    filter: {
      chain: CHAIN_TYPE.CRONOS,
      collections: [nftCollectionAddress],
      listingType: "ALL",
    },
  }

  return userCollectionParams
}

export const useGetCronosCruiserCollections = (
  queryOptions?: Omit<
    UseQueryOptions<
      UserNftCollectionsMeta,
      unknown,
      UserNftCollectionsMeta,
      any[]
    >,
    "queryFn" | "queryKey"
  >,
) => {
  const userCollectionParams: CollectionParams = useGetUserCronosCruiserParams()

  const { data: userNftCollections, isLoading: isUserNftCollectionsLoading } =
    useUserNftCollections(userCollectionParams, {
      ...queryOptions,
      enabled: userCollectionParams.address !== AddressZero,
    })

  return {
    data: userNftCollections,
    isLoading: isUserNftCollectionsLoading,
  }
}

export const useGetNftCollections = (
  queryOptions?: Omit<
    UseQueryOptions<
      UserNftCollectionsMeta,
      unknown,
      UserNftCollectionsMeta,
      any[]
    >,
    "queryFn" | "queryKey"
  >,
) => {
  const userCollectionParams = useGetUserCronosCruiserParams()

  const {
    data: userNftCollections,
    isLoading: isUserNftCollectionsLoading,
    isFetchingNextPage,
    isFetching,
  } = useUserAllNftCollections(userCollectionParams, {
    ...queryOptions,
    enabled: userCollectionParams.address !== AddressZero,
  })

  return {
    data: userNftCollections,
    isLoading: isUserNftCollectionsLoading,
    isFetching,
    isFetchingNextPage,
  }
}

export const useFormatLayerForNftDetails = () => {
  const {
    data: userAllNftCollections,
    isLoading: isUserNftCollectionsLoading,
    isFetching,
    isFetchingNextPage,
  } = useGetNftCollections()

  const allEdges = _.flatten(
    userAllNftCollections?.pages.map((page) => {
      const { edges } = page
      return edges ?? []
    }) ?? [],
  )

  const formattedNftList = allEdges?.map(({ node }) => {
    return {
      id: node.tokenId,
      imgUrl: node?.image?.url,
      name: node.name,
      rarity: node?.rarityRank,
      attributes: [],
    }
  })

  return {
    data: formattedNftList,
    isLoading: isUserNftCollectionsLoading,
    isFetching,
    isFetchingNextPage,
  }
}

export const useFormatLayerForStakedNftDetails = (
  tokenIds: string[],
  queryOptions?: Partial<Pick<UseQueryOptions, "enabled">>,
) => {
  const { chainId } = useActiveWeb3React()
  const nftAddress =
    (chainId && CRONOS_CRUISER_CONTRACT_ADDRESSES[chainId]) || AddressZero
  const results = useNftsAssetInfo(
    {
      nftAddress,
      chain: CHAIN_TYPE.CRONOS,
      tokenIds,
    },
    queryOptions,
  )

  let loadingCount = 0

  const formattedResults = results.map((result, i) => {
    if (result?.isLoading) {
      loadingCount += 1
    }
    return {
      id: result?.data?.tokenId ?? tokenIds[i],
      name: result?.data?.name ?? "",
      imgUrl: result?.data?.image?.url ?? "",
      rarity: result?.data?.rarityRank,
      attributes: [],
    }
  })

  return {
    data: formattedResults,
    isLoading: loadingCount !== 0,
  }
}

export default useGetCronosCruiserCollections
